import { useState, useCallback } from "react";
import axios from "axios";
import moment from "moment-timezone";
import * as constants from "../../../utils/constants";

const API_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;

/**
 * Hook for interacting with Outlook Calendar API
 */
export const useOutlookCalendar = (
  connectionId = null,
  { targetUserId = null } = {},
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("authToken");
    return {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    };
  }, []);

  // Map Microsoft timezone names to IANA timezone names
  const msToIanaTimezone = {
    UTC: "UTC",
    "Etc/UTC": "UTC",
    "India Standard Time": "Asia/Kolkata",
    "Pacific Standard Time": "America/Los_Angeles",
    "Pacific Daylight Time": "America/Los_Angeles",
    "Eastern Standard Time": "America/New_York",
    "Eastern Daylight Time": "America/New_York",
    "Central Standard Time": "America/Chicago",
    "Central Daylight Time": "America/Chicago",
    "Mountain Standard Time": "America/Denver",
    "Mountain Daylight Time": "America/Denver",
    "GMT Standard Time": "Europe/London",
    "British Summer Time": "Europe/London",
    "Central European Standard Time": "Europe/Berlin",
    "Central European Summer Time": "Europe/Berlin",
    "W. Europe Standard Time": "Europe/Berlin",
    "Romance Standard Time": "Europe/Paris",
    "Singapore Standard Time": "Asia/Singapore",
    "Tokyo Standard Time": "Asia/Tokyo",
    "China Standard Time": "Asia/Shanghai",
    "AUS Eastern Standard Time": "Australia/Sydney",
    "E. Australia Standard Time": "Australia/Brisbane",
    "Arabian Standard Time": "Asia/Dubai",
    "Israel Standard Time": "Asia/Jerusalem",
  };

  // Parse datetime from Graph API and convert to viewer's local timezone
  const parseGraphDateTime = (dateTimeObj) => {
    if (!dateTimeObj) return null;

    // If it's already a string, return as-is
    if (typeof dateTimeObj === "string") return dateTimeObj;

    const dateTime =
      dateTimeObj.dateTime?.replace(/\.\d+$/, "") || dateTimeObj.dateTime;
    const msTimeZone = dateTimeObj.timeZone || "UTC";

    // Convert MS timezone to IANA format
    const ianaTimeZone = msToIanaTimezone[msTimeZone] || msTimeZone;

    try {
      // Parse the datetime in its original timezone using moment-timezone
      // This interprets the datetime as being in the specified timezone
      const eventMoment = moment.tz(dateTime, ianaTimeZone);

      // Convert to viewer's local timezone and return ISO string
      // The browser/react-big-calendar will display this in local time
      return eventMoment.toDate().toISOString();
    } catch (e) {
      console.warn(`Timezone conversion failed for ${msTimeZone}:`, e);
      // Fallback: assume UTC
      return dateTime + "Z";
    }
  };

  // Transform event from API format to FullCalendar format
  const transformEvent = (event) => ({
    id: event.id,
    title: event.subject,
    start: parseGraphDateTime(event.start),
    end: parseGraphDateTime(event.end),
    allDay: event.isAllDay || false,
    extendedProps: {
      location: event.location?.displayName || "",
      description: event.bodyPreview || event.body?.content || "",
      organizer: event.organizer?.emailAddress?.address || "",
      attendees: event.attendees || [],
      webLink: event.webLink,
      onlineMeetingUrl: event.onlineMeetingUrl,
      isOnlineMeeting: event.isOnlineMeeting,
      importance: event.importance,
      showAs: event.showAs,
      responseStatus: event.responseStatus?.response,
      timeZone: event.start?.timeZone || "UTC",
    },
  });

  // List calendars
  const listCalendars = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (connectionId) params.connection_id = connectionId;
      if (targetUserId) params.target_user_id = targetUserId;
      const response = await axios.get(`${API_URL}/api/v1/outlook/calendars/`, {
        headers: getAuthHeaders(),
        params,
      });
      return response.data.calendars || [];
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch calendars");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, connectionId, targetUserId]);

  // Get events for a date range
  const getEvents = useCallback(
    async (startDate, endDate, calendarId = null) => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          start_date: startDate,
          end_date: endDate,
          ...(calendarId && { calendar_id: calendarId }),
          ...(connectionId && { connection_id: connectionId }),
          ...(targetUserId && { target_user_id: targetUserId }),
        };
        const response = await axios.get(`${API_URL}/api/v1/outlook/events/`, {
          headers: getAuthHeaders(),
          params,
        });
        return (response.data.events || []).map(transformEvent);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch events");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders, connectionId, targetUserId],
  );

  // Get single event
  const getEvent = useCallback(
    async (eventId) => {
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (connectionId) params.connection_id = connectionId;
        if (targetUserId) params.target_user_id = targetUserId;
        const response = await axios.get(
          `${API_URL}/api/v1/outlook/events/${eventId}/`,
          { headers: getAuthHeaders(), params },
        );
        return transformEvent(response.data);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch event");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders, connectionId, targetUserId],
  );

  // Create event
  const createEvent = useCallback(
    async (eventData) => {
      setLoading(true);
      setError(null);
      try {
        // Parse attendees string into array of emails
        let attendees = [];
        if (eventData.attendees && typeof eventData.attendees === "string") {
          attendees = eventData.attendees
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email.length > 0);
        } else if (Array.isArray(eventData.attendees)) {
          attendees = eventData.attendees;
        }

        // Get user's local timezone from browser
        const userTimezone =
          eventData.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone;

        const payload = {
          subject: eventData.title,
          start: eventData.start,
          end: eventData.end,
          timezone: userTimezone, // Include timezone for correct time interpretation
          is_all_day: eventData.allDay || false,
          ...(eventData.location && { location: eventData.location }),
          ...(eventData.description && { body: eventData.description }),
          ...(attendees.length > 0 && { attendees }),
          ...(eventData.isOnlineMeeting && {
            is_online_meeting: eventData.isOnlineMeeting,
          }),
          ...(eventData.calendarId && { calendar_id: eventData.calendarId }),
          ...(connectionId && { connection_id: connectionId }),
        };
        const response = await axios.post(
          `${API_URL}/api/v1/outlook/events/`,
          payload,
          { headers: getAuthHeaders() },
        );
        return transformEvent(response.data);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to create event");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders],
  );

  // Update event
  const updateEvent = useCallback(
    async (eventId, eventData) => {
      setLoading(true);
      setError(null);
      try {
        // Get user's local timezone from browser
        const userTimezone =
          eventData.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone;

        const payload = { timezone: userTimezone };
        if (eventData.title) payload.subject = eventData.title;
        if (eventData.start) payload.start = eventData.start;
        if (eventData.end) payload.end = eventData.end;
        if (eventData.location) payload.location = eventData.location;
        if (eventData.description) payload.body = eventData.description;
        if (eventData.allDay !== undefined)
          payload.is_all_day = eventData.allDay;

        const params = {};
        if (connectionId) params.connection_id = connectionId;
        const response = await axios.patch(
          `${API_URL}/api/v1/outlook/events/${eventId}/`,
          payload,
          { headers: getAuthHeaders(), params },
        );
        return transformEvent(response.data);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to update event");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders],
  );

  // Delete event
  const deleteEvent = useCallback(
    async (eventId) => {
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (connectionId) params.connection_id = connectionId;
        await axios.delete(`${API_URL}/api/v1/outlook/events/${eventId}/`, {
          headers: getAuthHeaders(),
          params,
        });
        return true;
      } catch (err) {
        setError(err.response?.data?.error || "Failed to delete event");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders],
  );

  // Check free/busy
  const getFreeBusy = useCallback(
    async (emails, startTime, endTime, interval = 30) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.post(
          `${API_URL}/api/v1/outlook/availability/`,
          { emails, start_time: startTime, end_time: endTime, interval },
          { headers: getAuthHeaders() },
        );
        return response.data;
      } catch (err) {
        setError(err.response?.data?.error || "Failed to check availability");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders],
  );

  return {
    loading,
    error,
    listCalendars,
    getEvents,
    getEvent,
    createEvent,
    updateEvent,
    deleteEvent,
    getFreeBusy,
  };
};

export default useOutlookCalendar;
